import { useState } from 'react';
import { useApi } from '../lib/useApi.ts';
import { percent, shortDate } from '../lib/format.ts';
import { Badge, Button, Card, EmptyState, Skeleton, TBody, TD, TH, THead, TR, Table } from '../components/ui/index.ts';
import { CheckIcon, SparkIcon } from '../components/icons.tsx';

interface Ranked {
  label: string;
  count: number;
  share: number;
}

interface Lethality {
  type: string;
  raised: number;
  won: number;
  lost: number;
  close_rate: number | null;
}

interface Insight {
  id: number;
  kind: string;
  period_start: string;
  period_end: string;
  sample_size: number;
  generated_at: string;
  markdown: string;
  content: {
    sample: { analyses: number; leads: number };
    stated_vs_true_gap_rate: number | null;
    bottlenecks: Ranked[];
    objection_lethality: Lethality[];
    service_interests: Ranked[];
    language_bank: { quote: string; why: string; lead: string }[];
    gap_examples: { lead: string; stated: string; truth: string; gap: string }[];
  };
}

/**
 * The portfolio view. The markdown is the deliverable: it exists to be
 * pasted into an email to Adam, so copying it is the primary action.
 */
export default function Insights() {
  const { data, error, loading } = useApi<{ insight: Insight }>('/insights/latest');
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!data) return;
    await navigator.clipboard.writeText(data.insight.markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  if (loading) return <Skeleton className="h-64 w-full" />;

  if (error || !data) {
    return (
      <EmptyState
        icon={<SparkIcon size={18} />}
        title="No insights generated yet"
        description="The weekly pass runs Monday morning. To generate one now, run: npm run job:generate-insights"
      />
    );
  }

  const { insight } = data;
  const c = insight.content;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tighter">Voice of the Doctor</h1>
          <p className="mt-0.5 text-xs text-fg-subtle">
            {shortDate(insight.period_start)} to {shortDate(insight.period_end)} ·{' '}
            {c.sample.analyses} analysed calls across {c.sample.leads} people
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          leading={copied ? <CheckIcon size={13} /> : undefined}
          onClick={() => void copy()}
        >
          {copied ? 'Copied' : 'Copy markdown for Adam'}
        </Button>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <h2 className="text-sm font-medium text-fg">What is actually blocking them</h2>
          <p className="mt-0.5 text-xs text-fg-subtle">Ranked by the true bottleneck, not the stated one.</p>
          <ul className="mt-3 space-y-2">
            {c.bottlenecks.slice(0, 8).map((item) => (
              <li key={item.label} className="flex gap-3 text-sm">
                <span className="numeric w-8 shrink-0 text-right font-medium text-accent-text">
                  {item.count}×
                </span>
                <span className="min-w-0 text-fg-muted">{item.label}</span>
              </li>
            ))}
            {c.bottlenecks.length === 0 && (
              <li className="text-xs text-fg-subtle">Not enough analysed calls yet.</li>
            )}
          </ul>
        </Card>

        <Card>
          <h2 className="text-sm font-medium text-fg">Objection lethality</h2>
          <p className="mt-0.5 text-xs text-fg-subtle">
            Which objections actually kill deals, and which are just noise.
          </p>
          {c.objection_lethality.length === 0 ? (
            <p className="mt-3 text-xs text-fg-subtle">No objections recorded in this period.</p>
          ) : (
            <div className="mt-3">
              <Table>
                <THead>
                  <TH>Objection</TH>
                  <TH numeric>Raised by</TH>
                  <TH numeric>Close rate</TH>
                </THead>
                <TBody>
                  {c.objection_lethality.slice(0, 8).map((item) => (
                    <TR key={item.type}>
                      <TD>{item.type}</TD>
                      <TD numeric muted>{item.raised}</TD>
                      <TD numeric>
                        {item.close_rate === null ? (
                          <span className="text-fg-faint">none resolved</span>
                        ) : (
                          <Badge tone={item.close_rate >= 0.5 ? 'won' : 'lost'}>
                            {percent(item.close_rate)}
                          </Badge>
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
              <p className="mt-2 text-2xs text-fg-subtle">
                Counts people, deduped: raising price on three calls is one person.
              </p>
            </div>
          )}
        </Card>
      </div>

      <Card>
        <h2 className="text-sm font-medium text-fg">Where they were wrong about themselves</h2>
        <p className="mt-0.5 text-xs text-fg-subtle">
          Stated bottleneck differed from the true one in{' '}
          <span className="text-accent-text">{percent(c.stated_vs_true_gap_rate)}</span> of calls
          where they named one.
        </p>
        <div className="mt-4 space-y-4">
          {c.gap_examples.map((example) => (
            <div key={example.lead} className="rounded-md border border-border-subtle bg-surface-inset p-3">
              <p className="text-sm font-medium text-fg">{example.lead}</p>
              <dl className="mt-2 space-y-1.5 text-xs">
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 text-fg-subtle">They said</dt>
                  <dd className="text-fg-muted">{example.stated}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 text-fg-subtle">It was</dt>
                  <dd className="text-fg-muted">{example.truth}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 text-fg-subtle">Gap</dt>
                  <dd className="text-accent-text">{example.gap}</dd>
                </div>
              </dl>
            </div>
          ))}
          {c.gap_examples.length === 0 && (
            <p className="text-xs text-fg-subtle">No gaps recorded in this period.</p>
          )}
        </div>
      </Card>

      <Card>
        <h2 className="text-sm font-medium text-fg">In their own words</h2>
        <p className="mt-0.5 text-xs text-fg-subtle">Verbatim. Never paraphrased.</p>
        <ul className="mt-4 space-y-3">
          {c.language_bank.slice(0, 10).map((entry, index) => (
            <li key={index} className="border-l-2 border-border pl-3">
              <p className="text-sm leading-relaxed text-fg-muted">"{entry.quote}"</p>
              <p className="mt-1 text-2xs text-fg-subtle">
                {entry.lead} · {entry.why}
              </p>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
